import { useEffect, useState } from "react";

import { api } from "../lib/api";
import type { DuckdbMetrics } from "../lib/types";

/**
 * Intel & Ops — readouts from loop-driver's DuckDB analytical store.
 *
 * The bot's `scripts/push_duckdb_metrics.py` condenses the tailer-fed
 * DuckDB tables (pulse_events / decisions / spawn_audit / positions_meta)
 * into one snapshot and pushes it. This panel renders the headline
 * numbers: market-signal frequency, guardrail-decision analytics, and
 * session-fleet reliability. All sections are optional — render what's
 * present, ignore the rest.
 */

function formatRelative(iso: string | null): string {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const deltaMin = Math.round((Date.now() - t) / 60_000);
  if (deltaMin < 1) return "just now";
  if (deltaMin < 60) return `${deltaMin} min ago`;
  if (deltaMin < 60 * 24) return `${Math.round(deltaMin / 60)}h ago`;
  return `${Math.round(deltaMin / (60 * 24))}d ago`;
}

const KIND_COLOR: Record<string, string> = {
  range_break_up: "text-emerald-300",
  range_break_dn: "text-rose-300",
  fast_move: "text-amber-300",
  vol_surge: "text-sky-300",
};

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-md border border-ink-700 bg-ink-800/40 px-3 py-2">
      <div className={`font-mono text-lg ${accent ?? "text-slate-100"}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{title}</div>
      {children}
    </div>
  );
}

export function IntelOpsPanel() {
  const [m, setM] = useState<DuckdbMetrics | null>(null);
  const [computedAt, setComputedAt] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.duckdbMetrics();
        if (!cancelled) {
          setM(r.metrics);
          setComputedAt(r.computed_at_utc);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "failed");
      }
    };
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const sp = m?.signal_pulse;
  const dec = m?.decisions;
  const fleet = m?.fleet;
  const pos = m?.positions;

  return (
    <section className="rounded-lg border border-ink-700 bg-ink-900">
      <div className="flex items-baseline justify-between border-b border-ink-700 px-3 py-2">
        <div className="text-sm">
          <span className="font-semibold text-slate-200">📊 Intel &amp; Ops</span>
          <span className="ml-2 text-xs font-normal text-slate-500">DuckDB readouts</span>
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">
          computed {formatRelative(computedAt)}
        </div>
      </div>

      {err && (
        <div className="border-b border-ink-700 px-3 py-2 text-xs text-accent-err">{err}</div>
      )}

      {!m ? (
        <div className="px-3 py-6 text-center text-sm text-slate-500">
          No DuckDB metrics yet.
          <div className="mt-1 text-xs">
            loop-driver pushes a snapshot from the analytical store
            (pulse_events / decisions / spawn_audit / positions_meta).
          </div>
        </div>
      ) : (
        <div className="grid gap-4 px-3 py-3 md:grid-cols-2">
          {sp && (
            <Section title="📡 Signal Pulse (range breaks & moves)">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="events tracked" value={sp.total_events.toLocaleString()} />
                <Stat label="last 24h" value={sp.last24h_total.toLocaleString()} accent="text-sky-300" />
                <Stat label="symbols" value={String(sp.symbols)} />
              </div>
              <ul className="space-y-1 text-xs">
                {sp.by_kind.map((k) => (
                  <li key={k.kind} className="flex items-baseline justify-between">
                    <span className={`font-mono ${KIND_COLOR[k.kind] ?? "text-slate-300"}`}>
                      {k.kind}
                    </span>
                    <span className="font-mono text-slate-400">
                      {k.n.toLocaleString()}
                      <span className="ml-2 text-slate-600">({k.last24h} /24h)</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {dec && (
            <Section title="🛡️ Guardrail Decisions">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="total" value={dec.total.toLocaleString()} />
                <Stat label="allowed" value={`${dec.allow_pct}%`} accent="text-emerald-300" />
                <Stat label="denied" value={dec.denied.toLocaleString()} accent="text-rose-300" />
              </div>
              <ul className="space-y-1 text-xs">
                {dec.by_strategy.map((s) => (
                  <li key={s.strategy} className="flex items-baseline justify-between">
                    <span className="text-slate-300">{s.strategy}</span>
                    <span className="font-mono text-slate-400">
                      {s.n.toLocaleString()}
                      <span className="ml-2 text-slate-600">({s.allow_pct}% ok)</span>
                    </span>
                  </li>
                ))}
              </ul>
              {dec.top_denials && dec.top_denials.length > 0 && (
                <div className="mt-1 border-t border-ink-700/50 pt-1 text-[11px] text-slate-500">
                  <span className="uppercase tracking-wider">top blocks:</span>{" "}
                  {dec.top_denials.slice(0, 3).map((d, i) => (
                    <span key={i} className="text-slate-400">
                      {i > 0 && " · "}
                      {d.reason} ({d.n})
                    </span>
                  ))}
                </div>
              )}
            </Section>
          )}

          {fleet && (
            <Section title="🛰️ Fleet Reliability (session spawns)">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="spawns" value={fleet.spawns_ok.toLocaleString()} accent="text-emerald-300" />
                <Stat label="clean kills" value={fleet.kills_ok.toLocaleString()} />
                <Stat
                  label="dark-out blocks"
                  value={fleet.auth_401_blocks.toLocaleString()}
                  accent={fleet.auth_401_blocks > 0 ? "text-amber-300" : "text-slate-100"}
                />
              </div>
              {fleet.by_role && fleet.by_role.length > 0 && (
                <ul className="space-y-1 text-xs">
                  {fleet.by_role.map((r) => (
                    <li key={r.role} className="flex items-baseline justify-between">
                      <span className="text-slate-300">{r.role}</span>
                      <span className="font-mono text-slate-400">{r.n} spawns</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          )}

          {pos && (
            <Section title="📦 Position Telemetry">
              <div className="grid grid-cols-2 gap-2">
                <Stat label="entries logged" value={pos.total.toLocaleString()} />
                <Stat label="symbols" value={String(pos.symbols)} />
              </div>
              {pos.by_strategy && pos.by_strategy.length > 0 && (
                <ul className="space-y-1 text-xs">
                  {pos.by_strategy.map((s) => (
                    <li key={s.strategy} className="flex items-baseline justify-between">
                      <span className="text-slate-300">{s.strategy}</span>
                      <span className="font-mono text-slate-400">{s.n.toLocaleString()} entries</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          )}
        </div>
      )}

      {m?.db_totals && (
        <div className="border-t border-ink-700 px-3 py-2 text-[10px] text-slate-600">
          <span className="uppercase tracking-wider">db rows:</span>{" "}
          {Object.entries(m.db_totals).map(([k, v], i) => (
            <span key={k}>
              {i > 0 && " · "}
              <span className="text-slate-500">{k}</span>{" "}
              <span className="font-mono text-slate-400">{v.toLocaleString()}</span>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
